import { useMemo, useRef, useState, type ChangeEvent } from 'react';
import { useSelector } from 'react-redux';
import { Formik, Form } from 'formik';
import { Box, Text, Button, Modal } from '@rsos/base-components';
import type { MultiInputTag } from '@rsos/components/capstone/base/form/inputs/MultiEntryInput/MultiEntryInput';
import { ErrorComponent } from '@rsos/components/capstone/base/form/inputs/MultiEntryInput/MultiEntryInput.styles';
import { GPX_FEATURES } from '@rsos/gainsight';
import { setConversationLanguage } from '@rsos/reduxDataStore/src/features/chats/chatsSlice';
import { TranslationLanguage } from '@rsos/reduxDataStore/src/features/chats/chatsTypes';
import { setSelectedCallerID } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import {
  expand,
  setSelectedTab,
} from '@rsos/reduxDataStore/src/features/eventPane/eventPaneSlice';
import {
  OUTBOUND_SMS_CARD,
  closeCard,
} from '@rsos/reduxDataStore/src/features/mapCard/mapCardSlice';
import { type PreconfiguredResponse } from '@rsos/reduxDataStore/src/features/preconfiguredMessages/preconfiguredMessagesAPI';
import {
  useCreateConversationOutboundSMSMutation,
  useLazyGetConversationMetadataQuery,
} from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Api';
import {
  setSelectedConversation,
  setPendingTF911Message,
} from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Slice';
import { selectUserOrgByApp } from '@rsos/sinatra';
import type { SmartPhraseContext } from '@rsos/utils/SmartPhrases';
import {
  formatInternationalPhoneNumberE164,
  isValidInternationalPhoneNumber,
} from '@rsos/utils/internationalPhoneNumberUtils';
import { useAppDispatch, type RootState } from '../../../../app/rootReducer';
import AllMessages from '../../../PreconfiguredMessages/AllMessages/AllMessages';
import {
  PRECONFIG_REQUEST_LOCATION,
  handleDynamicMessage,
} from '../../../PreconfiguredMessages/AllMessages/utils';
import SuggestedResponses from '../../../PreconfiguredMessages/SuggestedResponses';
import { useEnabledPreconfiguredMessages } from '../../../PreconfiguredMessages/useEnabledPreconfiguredMessages';
import RequestLocation from '../../../RequestLocation';
import { TF911InitiationTranslationMenu } from '../../CallChats/TF911ChatBox/TF911InitiationTranslationMenu';
import {
  trackSendOutboundSMSClick,
  trackStartTF911Response,
} from '../../CallChats/textFrom911Trackings';
import {
  INVALID_NUMBER,
  MSG_REQUEST_ERROR,
  INVALID_NUMBER_ERROR,
  MESSAGE_FAILED_TO_SEND,
  NUMBER_ALREADY_CALLED_911,
  EXISTING_CONVERSATION_ERROR,
} from './constants';
import {
  initialValues,
  validationSchema,
  initialErrorState,
  isBtnDisabled,
  containsCallerIdBoundUrl,
  type OutboundSMSValues,
  type ConversationMutationTuple,
} from './helpers';
import {
  Container,
  PhoneNumberContainer,
  SendMsgContainer,
  TextArea,
  TextAreaRow,
  CustomMultiEntryInput,
  TextAreaContainer,
} from './styles/OutboundSMS.styles';

const PrefixComponent = (
  <Text
    pl="6px"
    mb="5px"
    size="normal"
    color="secondaryText"
    wordBreak="keep-all"
  >
    To:
  </Text>
);

export const OutboundSMS = () => {
  const textAreaContainerRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const [phoneNumberTags, setPhoneNumberTags] = useState<MultiInputTag[]>([]);
  const [errorMsgs, setErrorMsgs] = useState(initialErrorState);
  const [isAllMessagesOpen, setIsAllMessagesOpen] = useState(false);
  const [locationError, setLocationError] = useState(false);
  const [pendingInitiationLanguage, setPendingInitiationLanguage] =
    useState<TranslationLanguage | null>(null);

  const orgName = useSelector(
    (state: RootState) =>
      selectUserOrgByApp(
        state.sinatra,
        state.sinatra.user.currentRole?.application || '',
      )?.name,
  );

  const enabledPreconfiguredMessages = useEnabledPreconfiguredMessages(
    orgName,
    { hasRequestLocation: true },
  );

  const hasRequestLocationAccess = useMemo(
    () =>
      enabledPreconfiguredMessages?.some(
        msg => msg.title === PRECONFIG_REQUEST_LOCATION,
      ) ?? false,
    [enabledPreconfiguredMessages],
  );

  const setReplicatedValue = (value: string) => {
    const container = textAreaContainerRef.current as HTMLDivElement | null;
    if (container?.dataset) {
      container.dataset.replicatedValue = value;
    }
  };

  const handleChange = (
    e: ChangeEvent<HTMLTextAreaElement>,
    setFieldValue: (field: string, value: unknown) => void,
  ) => {
    const { value } = e.target;
    setFieldValue('initialOutboundMessage', value);
    setErrorMsgs(prev => ({ ...prev, [MSG_REQUEST_ERROR]: false }));
    setReplicatedValue(value);
  };

  const [createConversation, conversationResult] =
    useCreateConversationOutboundSMSMutation() as unknown as ConversationMutationTuple;
  const [getConversationMetadata] = useLazyGetConversationMetadataQuery();

  const dispatchLanguage = async (
    conversationId: string | undefined,
    language: TranslationLanguage | null,
  ) => {
    if (!language || !conversationId) return;
    await dispatch(
      setConversationLanguage({
        conversationId,
        selectedLanguageCode: language.languageCode,
        outboundTranslation: true,
      }),
    );
  };

  const handleExistingConversation = async (
    callerID: string,
    message: string,
    savedLanguage: TranslationLanguage | null,
  ) => {
    const metadata = await getConversationMetadata({
      callerID,
      orgName,
      custom: { isOutboundSMS: true },
    });
    const metadataResult = metadata as {
      isSuccess?: boolean;
      data?: { conversation_id?: string };
    };
    if (!metadataResult?.isSuccess) return;

    const conversationId = metadataResult.data?.conversation_id;
    await dispatchLanguage(conversationId, savedLanguage);
    dispatch(setSelectedCallerID(callerID));
    dispatch(setSelectedConversation(callerID));
    dispatch(setPendingTF911Message(message));
    dispatch(setSelectedTab('chat'));
    dispatch(closeCard(OUTBOUND_SMS_CARD));
    dispatch(expand());
  };

  const handleSubmit = async (values: OutboundSMSValues) => {
    const phoneNumberTag = values?.phoneNumberTags?.[0];
    const message = values?.initialOutboundMessage;
    const callerID =
      formatInternationalPhoneNumberE164(
        phoneNumberTag?.value ?? '',
      )?.substring(1) ?? '';

    trackSendOutboundSMSClick();

    const resp = await createConversation({
      callerID,
      message: message || undefined,
      orgName,
      languageCode: pendingInitiationLanguage?.languageCode,
    });

    trackStartTF911Response({
      feature: GPX_FEATURES.TF911_OUTBOUND,
      responseSuccess: !resp?.error,
      conversationId: resp?.data?.conversation_id ?? '',
    });

    if (resp?.error?.status === 409) {
      const savedLanguage = pendingInitiationLanguage;
      setPendingInitiationLanguage(null);
      await handleExistingConversation(callerID, message, savedLanguage);
      return;
    }

    if (resp?.error) {
      setErrorMsgs(prev => ({ ...prev, [MSG_REQUEST_ERROR]: true }));
      return;
    }

    await dispatchLanguage(
      resp?.data?.conversation_id,
      pendingInitiationLanguage,
    );
    setPendingInitiationLanguage(null);
    // TODO: logic on success
    dispatch(closeCard(OUTBOUND_SMS_CARD));
    dispatch(setSelectedCallerID(callerID));
    dispatch(setSelectedTab('chat'));
    dispatch(expand());
  };

  const inputError = useMemo(() => {
    if (errorMsgs[INVALID_NUMBER_ERROR]) {
      return INVALID_NUMBER;
    }
    return null;
  }, [errorMsgs]);

  const handleCopyToChat = (
    msg: PreconfiguredResponse,
    setFieldValue: (field: string, value: unknown) => void,
  ) => {
    setFieldValue('initialOutboundMessage', msg.body);
    setReplicatedValue(msg.body);
    setIsAllMessagesOpen(false);
  };

  const handleRequestLocation = async (
    locationData: SmartPhraseContext,
    setFieldValue: (field: string, value: unknown) => void,
  ) => {
    setLocationError(false);
    const callerID =
      formatInternationalPhoneNumberE164(
        phoneNumberTags[0]?.value ?? '',
      )?.substring(1) ?? '';

    const locationMessage = enabledPreconfiguredMessages?.find(
      msg => msg.title === PRECONFIG_REQUEST_LOCATION,
    );

    if (locationMessage) {
      try {
        const { body } = await handleDynamicMessage(locationMessage, {
          ...locationData,
          callerID,
        });
        setFieldValue('initialOutboundMessage', body);
        setReplicatedValue(body);
      } catch {
        setLocationError(true);
      }
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validateOnMount={true}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ values, setFieldValue, errors }) => {
        const hasInvalidPhoneNumber =
          !values.phoneNumberTags?.length || !!errors.phoneNumberTags;

        const handleUpdateTag = (tags: MultiInputTag[]) => {
          setPhoneNumberTags(tags);
          setErrorMsgs(prev => ({
            ...prev,
            [INVALID_NUMBER_ERROR]: false,
            [NUMBER_ALREADY_CALLED_911]: false,
            [EXISTING_CONVERSATION_ERROR]: false,
          }));
          if (containsCallerIdBoundUrl(values.initialOutboundMessage)) {
            setFieldValue('initialOutboundMessage', '');
            setReplicatedValue('');
          }
        };

        const handleSendMessage = async (msg: PreconfiguredResponse) => {
          setIsAllMessagesOpen(false);
          await handleSubmit({ ...values, initialOutboundMessage: msg.body });
        };

        return (
          <Form id="outbound-sms">
            <Container>
              <PhoneNumberContainer>
                <TF911InitiationTranslationMenu
                  selectedLanguage={pendingInitiationLanguage}
                  onLanguageSelect={setPendingInitiationLanguage}
                />
                <Box mt={2}>
                  <Text fontWeight="bold" size="large" color="primaryText">
                    Start Chat
                  </Text>
                  <Text size="normal" color="secondaryText">
                    Chat with a phone number that did not call 911
                  </Text>
                </Box>
                <Box width="100%" py={3}>
                  <CustomMultiEntryInput
                    value={values.phoneNumber}
                    name="phoneNumber"
                    placeholder={
                      phoneNumberTags.length ? '' : '+1 555 555 5555'
                    }
                    tags={phoneNumberTags}
                    updateTags={handleUpdateTag}
                    setFieldValue={setFieldValue}
                    validator={isValidInternationalPhoneNumber}
                    inputType="tel"
                    hiddenInputName="phoneNumberTags"
                    error={
                      inputError ??
                      (typeof errors?.phoneNumberTags === 'string'
                        ? errors.phoneNumberTags
                        : undefined)
                    }
                    disabled={!!phoneNumberTags.length}
                    prefixComponent={PrefixComponent}
                  />
                </Box>
              </PhoneNumberContainer>
              <SendMsgContainer minHeight="60px">
                <TextAreaRow>
                  <TextAreaContainer ref={textAreaContainerRef}>
                    <TextArea
                      rows={1}
                      data-name="outbound-textbox"
                      name="initialOutboundMessage"
                      onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                        handleChange(e, setFieldValue)
                      }
                      placeholder="Message"
                      value={values.initialOutboundMessage}
                      error={
                        errors?.initialOutboundMessage ||
                        errorMsgs[MSG_REQUEST_ERROR]
                      }
                      disabled={conversationResult?.isLoading}
                    />
                    {errors?.initialOutboundMessage ? (
                      <ErrorComponent error={errors.initialOutboundMessage} />
                    ) : errorMsgs[MSG_REQUEST_ERROR] ? (
                      <ErrorComponent error={MESSAGE_FAILED_TO_SEND} />
                    ) : null}
                  </TextAreaContainer>
                  <Button
                    dataName="outbound-submit"
                    type="submit"
                    btnType="primary"
                    m={0}
                    disabled={isBtnDisabled(errors, values)}
                    isLoadingProp={conversationResult?.isLoading}
                  >
                    {values.initialOutboundMessage ? 'Send' : 'Start'}
                  </Button>
                </TextAreaRow>
                <Box horizontal align="center" justify="flex-end" width="100%">
                  {hasRequestLocationAccess && (
                    <RequestLocation
                      isOutboundSMS
                      onClick={locationData =>
                        handleRequestLocation(locationData, setFieldValue)
                      }
                      disabled={hasInvalidPhoneNumber}
                    />
                  )}
                  <SuggestedResponses
                    isOutboundSMS
                    handleViewClick={() => setIsAllMessagesOpen(true)}
                  />
                </Box>
                {locationError && (
                  <Text color="lightDanger" size="small">
                    Something went wrong, please try again
                  </Text>
                )}
              </SendMsgContainer>
            </Container>
            <Modal
              isOpened={isAllMessagesOpen}
              id="outbound-all-messages-modal"
              padding="0"
              width="448px"
            >
              <AllMessages
                preconfiguredMessages={enabledPreconfiguredMessages}
                onClose={() => setIsAllMessagesOpen(false)}
                onCopyToChat={msg => handleCopyToChat(msg, setFieldValue)}
                onSendMessage={handleSendMessage}
                isSendMessageDisabled={hasInvalidPhoneNumber}
              />
            </Modal>
          </Form>
        );
      }}
    </Formik>
  );
};
